import { computed, readonly, ref, toValue, watch, type MaybeRefOrGetter } from 'vue';
import type { SearchFilterPayload } from '@/composables/api/SearchApi';
import type { SortDirection } from '@/types/tableTypes';

/**
 * How the rows are chosen.
 *
 * - `ids`: the user ticked rows one by one. Only those ids are selected.
 * - `search`: the user chose "Select all N matching". Every match of the search is selected,
 *   except the rows the user unticked afterwards.
 */
export type BulkSelectionMode = 'ids' | 'search';

/**
 * The `mode` a "select all matching" selection sends.
 *
 * - `search`: every match of the entity's own search. Every bulk-selectable entity uses it.
 * - `list_search`: the schedules that pass a custom list search. The custom list page's
 *   schedule selection uses it, because the page searches lists, not schedules. Only the
 *   schedule entity accepts it.
 */
export type BulkSelectAllMode = 'search' | 'list_search';

/**
 * The `selection` node of a bulk-action request, exactly as the API contract describes it.
 *
 * `mode` is always present, so an empty `ids` array can never be read as "everything".
 */
export type BulkSelectionPayload =
  | { mode: 'ids'; ids: number[] }
  | {
      mode: BulkSelectAllMode;
      /** What the search request posts: `buildSearchFilterPayload(compositeFilter)`. Null means no filter. */
      filter: SearchFilterPayload | null;
      /** The page's `columnSort`. The server uses it only to order the report. */
      sort: Record<string, SortDirection>;
      /** The rows unticked after "Select all". */
      excluded_ids: number[];
      /**
       * The total the page showed, before `excluded_ids` are taken off. For `list_search` it is
       * the matching schedule count the bulk bar showed.
       */
      expected_count: number;
    };

/** The search on screen, as the page already builds it for its search request. */
export interface BulkSelectionSearchInputs {
  filter?: SearchFilterPayload | null;
  sort?: Record<string, SortDirection>;
}

export interface UseBulkSelectionOptions {
  /** The ids of the rows on the current page, in the order they are shown. */
  pageRowIds: MaybeRefOrGetter<readonly number[]>;
  /** The pager's cached total for the search on screen. */
  total: MaybeRefOrGetter<number>;
  /** The filter and sort the page searched with. Read when the payload is built. */
  searchInputs: MaybeRefOrGetter<BulkSelectionSearchInputs | null | undefined>;
  /**
   * Any value that changes when the entity, the filter or the sort changes.
   * The selection is cleared whenever it changes, the same way the page resets its pager.
   */
  resetKey: MaybeRefOrGetter<unknown>;
  /** The `mode` a "select all matching" payload sends. Defaults to `search`. */
  selectAllMode?: BulkSelectAllMode;
}

/** Only the modifier the range logic reads. A MouseEvent or KeyboardEvent both fit. */
type SelectionGesture = Pick<MouseEvent, 'shiftKey'>;

/** A row id as the API wants it: a whole number. Anything else is not selectable. */
export function toBulkSelectionId(value: unknown): number | null {
  const id = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  return Number.isInteger(id) ? id : null;
}

/**
 * The ticked rows on a search page, for bulk actions.
 *
 * The user ticks rows one at a time, or shift-clicks to tick a range on the current page.
 * Once the whole page is ticked, they can widen the selection to every match of the search.
 * After that, unticking a row adds it to a list of exclusions rather than to the chosen ids.
 *
 * Changing the entity, the filter or the sort clears everything. The rows on screen are then a
 * different result set, and a selection made against the old one would act on the wrong rows.
 */
export function useBulkSelection(options: UseBulkSelectionOptions) {
  const mode = ref<BulkSelectionMode>('ids');
  const chosenIds = ref<Set<number>>(new Set());
  const excludedIds = ref<Set<number>>(new Set());
  /** The row the last plain click toggled. A shift-click ticks everything between it and the new row. */
  const anchorId = ref<number | null>(null);

  const pageRowIds = computed(() => toValue(options.pageRowIds));
  const total = computed(() => toValue(options.total) || 0);

  function isSelected(rawId: unknown): boolean {
    const id = toBulkSelectionId(rawId);
    if (id === null) return false;
    return mode.value === 'search' ? !excludedIds.value.has(id) : chosenIds.value.has(id);
  }

  /** How many rows a bulk action would cover right now. */
  const count = computed(() =>
    mode.value === 'search'
      ? Math.max(0, total.value - excludedIds.value.size)
      : chosenIds.value.size,
  );

  const hasSelection = computed(() => count.value > 0);

  const selectedOnPageCount = computed(
    () => pageRowIds.value.filter((id) => isSelected(id)).length,
  );

  /** True once every row on the current page is ticked. An empty page is never "all ticked". */
  const isPageFullySelected = computed(
    () => pageRowIds.value.length > 0 && selectedOnPageCount.value === pageRowIds.value.length,
  );

  /** The state the page's own checkbox draws: all, none, or some. */
  const pageSelectionState = computed<boolean | 'indeterminate'>(() => {
    if (isPageFullySelected.value) return true;
    return selectedOnPageCount.value > 0 ? 'indeterminate' : false;
  });

  /**
   * Whether "select all matching" would add rows, even when nothing is ticked yet.
   * The custom list page offers "112 schedules" as soon as one list is ticked, so it reads this.
   */
  const hasUntickedMatches = computed(() => mode.value === 'ids' && total.value > chosenIds.value.size);

  /**
   * Whether to offer "Select all N matching".
   * It shows as soon as one row is ticked, and only when the search has more matches than are
   * ticked. Otherwise the offer would select nothing new.
   */
  const canSelectAllMatching = computed(() => hasUntickedMatches.value && chosenIds.value.size > 0);

  /** Tick or untick one row, in whichever mode is active. */
  function setOne(id: number, selected: boolean, chosen: Set<number>, excluded: Set<number>) {
    if (mode.value === 'search') {
      if (selected) excluded.delete(id);
      else excluded.add(id);
      return;
    }
    if (selected) chosen.add(id);
    else chosen.delete(id);
  }

  /**
   * Toggle one row.
   *
   * With shift held and an anchor row on this page, every row between the anchor and this row
   * takes this row's new state. That matches how a file manager extends a selection.
   */
  function toggle(rawId: unknown, event?: SelectionGesture) {
    const id = toBulkSelectionId(rawId);
    if (id === null) return;

    const nextState = !isSelected(id);
    const chosen = new Set(chosenIds.value);
    const excluded = new Set(excludedIds.value);

    const ids = pageRowIds.value;
    const anchorIndex = anchorId.value === null ? -1 : ids.indexOf(anchorId.value);
    const targetIndex = ids.indexOf(id);

    if (event?.shiftKey && anchorIndex !== -1 && targetIndex !== -1) {
      const [low, high] = anchorIndex <= targetIndex ? [anchorIndex, targetIndex] : [targetIndex, anchorIndex];
      for (const rangeId of ids.slice(low, high + 1)) {
        setOne(rangeId, nextState, chosen, excluded);
      }
    } else {
      setOne(id, nextState, chosen, excluded);
    }

    chosenIds.value = chosen;
    excludedIds.value = excluded;
    anchorId.value = id;
  }

  /** The page checkbox. It ticks the whole page, or unticks it when every row is already ticked. */
  function togglePage() {
    const nextState = !isPageFullySelected.value;
    const chosen = new Set(chosenIds.value);
    const excluded = new Set(excludedIds.value);
    for (const id of pageRowIds.value) {
      setOne(id, nextState, chosen, excluded);
    }
    chosenIds.value = chosen;
    excludedIds.value = excluded;
    anchorId.value = null;
  }

  /** Widen the selection to every match of the search. Nothing is excluded yet. */
  function selectAllMatching() {
    mode.value = 'search';
    chosenIds.value = new Set();
    excludedIds.value = new Set();
  }

  function clear() {
    mode.value = 'ids';
    chosenIds.value = new Set();
    excludedIds.value = new Set();
    anchorId.value = null;
  }

  /**
   * The `selection` node for a bulk-action request.
   * Null when nothing is selected, so a caller can never send an empty selection.
   */
  function buildSelectionPayload(): BulkSelectionPayload | null {
    if (!hasSelection.value) return null;

    if (mode.value === 'ids') {
      return { mode: 'ids', ids: [...chosenIds.value] };
    }

    const inputs = toValue(options.searchInputs);
    return {
      mode: options.selectAllMode ?? 'search',
      filter: inputs?.filter ?? null,
      sort: inputs?.sort ?? {},
      excluded_ids: [...excludedIds.value],
      expected_count: total.value,
    };
  }

  watch(
    () => toValue(options.resetKey),
    () => clear(),
  );

  return {
    mode: readonly(mode),
    chosenIds: readonly(chosenIds),
    excludedIds: readonly(excludedIds),
    count,
    hasSelection,
    isPageFullySelected,
    pageSelectionState,
    canSelectAllMatching,
    hasUntickedMatches,
    isSelected,
    toggle,
    togglePage,
    selectAllMatching,
    clear,
    buildSelectionPayload,
  };
}

export type BulkSelection = ReturnType<typeof useBulkSelection>;
